import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import type { PublicUser } from './types';

interface AuthContextValue {
  user: PublicUser | null;
  isLoading: boolean;
  /** Aggiorna la cache utente dopo login/registrazione/modifica profilo. */
  setUser: (user: PublicUser | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Promemoria di sessione: i cookie sono httpOnly (illeggibili da JS), quindi
 * senza questo hint OGNI pagina pubblica farebbe una chiamata /auth/me
 * destinata a un 401. '1' = probabilmente loggato, '0' = sicuramente no,
 * assente = mai verificato (si controlla una volta e si memorizza l'esito).
 * Cancellare i dati del sito azzera cookie E localStorage insieme: l'hint
 * non può restare "più informato" dei cookie.
 */
const SESSION_HINT = 'pc:sessione';
function readSessionHint(): '1' | '0' | null {
  try {
    const v = localStorage.getItem(SESSION_HINT);
    return v === '1' || v === '0' ? v : null;
  } catch {
    return null;
  }
}
function writeSessionHint(active: boolean) {
  try {
    localStorage.setItem(SESSION_HINT, active ? '1' : '0');
  } catch {
    // storage non disponibile: si farà la chiamata, nessun danno
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Sessione irrecuperabile (401 anche dopo il refresh): azzera l'utente
  // così RequireAuth riporta al login con ritorno alla pagina corrente.
  useEffect(() => {
    const onUnauthorized = () => {
      writeSessionHint(false);
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    };
    window.addEventListener('pc:unauthorized', onUnauthorized);
    return () => window.removeEventListener('pc:unauthorized', onUnauthorized);
  }, [queryClient]);

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      // Già saputo di non essere loggati: niente chiamata (e niente 401 in
      // console a ogni pagina pubblica).
      if (readSessionHint() === '0') return null;
      try {
        const res = await api<{ user: PublicUser }>('/auth/me');
        writeSessionHint(true);
        return res.user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          // Non autenticato: stato normale, non un errore.
          writeSessionHint(false);
          return null;
        }
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const value: AuthContextValue = {
    user: data ?? null,
    isLoading,
    setUser: (user) => {
      writeSessionHint(user !== null);
      queryClient.setQueryData(['auth', 'me'], user);
    },
    logout: async () => {
      await api('/auth/logout', { method: 'POST' });
      writeSessionHint(false);
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider');
  return ctx;
}
